import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import RobotTypes from "./pages/RobotTypes";
import RobotCategoryPage from "./pages/RobotCategoryPage";
import RobotSensors from "./pages/RobotSensors";
import RoboticsTopics from "./pages/RoboticsTopics";
import RoboticsEvents from "./pages/RoboticsEvents";
import ViralTopics from "./pages/ViralTopics";
import RoboticsNews from "./pages/RoboticsNews";
import RoboticsArticlePage from "./pages/RoboticsArticlePage";
import RoboticsXArticlePage from "./pages/RoboticsXArticlePage";
import BuildFirstRobotLimitedBudget from "./pages/BuildFirstRobotLimitedBudget";
import RoboticsWorldCountries from "./pages/RoboticsWorldCountries";
import About from "./pages/About";
import AuthorityHubPage from "./pages/AuthorityHubPage";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import CookiePolicy from "./pages/CookiePolicy";
import Advertise from "./pages/Advertise";
import Partnerships from "./pages/Partnerships";
import EditorialPolicy from "./pages/EditorialPolicy";
import NotFound from "./pages/NotFound";
import SearchConsoleTopics from "./pages/SearchConsoleTopics";
import RoboticArmJointComponents from "./pages/RoboticArmJointComponents";
import NuclearRobots from "./pages/NuclearRobots";
import AINews from "./pages/AINews";
import AIArticlePage from "./pages/AIArticlePage";
import RoboticsSeoClusterPage from "./pages/RoboticsSeoClusterPage";
import FrenchHumanoidRobots from "./pages/FrenchHumanoidRobots";
import JapaneseHumanoidRobots from "./pages/JapaneseHumanoidRobots";
import ChineseHumanoidRobots from "./pages/ChineseHumanoidRobots";
import AmericanHumanoidRobots from "./pages/AmericanHumanoidRobots";
import RoboticsCompaniesDirectory from "./pages/directories/RoboticsCompaniesDirectory";
import RoboticsCompanyRoutePage from "./pages/directories/RoboticsCompanyRoutePage";
import RoboticsCompanyCountryPage from "./pages/directories/RoboticsCompanyCountryPage";
import RoboticsSoftwareDirectory from "./pages/directories/RoboticsSoftwareDirectory";
import RoboticsSoftwareRoutePage from "./pages/directories/RoboticsSoftwareRoutePage";
import RobotComponentsDirectory from "./pages/directories/RobotComponentsDirectory";
import RobotComponentRoutePage from "./pages/directories/RobotComponentRoutePage";
import LocalizedPriorityPage from "./pages/LocalizedPriorityPage";
import LocalizedEditionHome from "./pages/LocalizedEditionHome";
import HumanoidCatalogPage from "./pages/humanoid/HumanoidCatalogPage";
import HumanoidManufacturersPage from "./pages/humanoid/HumanoidManufacturersPage";
import HumanoidManufacturerPage from "./pages/humanoid/HumanoidManufacturerPage";
import HumanoidCountriesPage from "./pages/humanoid/HumanoidCountriesPage";
import HumanoidCountryPage from "./pages/humanoid/HumanoidCountryPage";
import HumanoidRobotPage from "./pages/humanoid/HumanoidRobotPage";
import HumanoidIntelligenceHub from "./pages/humanoid/HumanoidIntelligenceHub";
import HumanoidDatabasePage from "./pages/humanoid/HumanoidDatabasePage";
import HumanoidComparePage from "./pages/humanoid/HumanoidComparePage";
import HumanoidTrackerPage from "./pages/humanoid/HumanoidTrackerPage";
import HumanoidCompanyGrowthPage from "./pages/humanoid/HumanoidCompanyGrowthPage";
import HumanoidInfographicHub from "./pages/humanoid-infographics/HumanoidInfographicHub";
import HumanoidInfographicPage from "./pages/humanoid-infographics/HumanoidInfographicPage";
import PhysicalAIIntelligencePage from "./pages/physical-ai/PhysicalAIIntelligencePage";
import JobsIntelligencePage from "./pages/jobs/JobsIntelligencePage";
import DataHub from "./pages/DataHub";
import ShopHumanoidRobots from "./pages/ShopHumanoidRobots";
import { humanoidInfographicIndex } from "./data/humanoid-infographics";
import SearchConsoleHubPage from "./pages/SearchConsoleHubPage";
import SearchConsoleLinkSection from "./components/SearchConsoleLinkSection";
import { searchConsoleHubPages } from "./data/searchConsoleIndexHubs";

const AdminCommunityPage = lazy(() => import("./pages/admin/AdminCommunityPage"));


const legacyRedirects = [
  [
    "/robotics/stardust-intelligence-real-time-humanoid-teleoperation",
    "/robotics/stardust-intelligence-humanoid-teleoperation",
  ],
  [
    "/robotics/perceptron-egocentric-annotation-wgo-bench",
    "/robotics/perceptron-egocentric-video-annotation-wgo-bench",
  ],
  ["/robot-dogs-quadrupeds", "/robot-types/robot-dogs-quadrupeds"],
  ["/industrial-robots", "/robot-types/industrial-robots"],
  ["/collaborative-robots-cobots", "/robot-types/collaborative-robots-cobots"],
  ["/warehouse-robots-amrs", "/robot-types/warehouse-robots-amrs"],
  ["/medical-surgical-robots", "/robot-types/medical-surgical-robots"],
  ["/agriculture-robots", "/robot-types/agriculture-robots"],
  ["/drones-aerial-robots", "/robot-types/drones-aerial-robots"],
  ["/service-robots", "/robot-types/service-robots"],
  ["/domestic-personal-robots", "/robot-types/domestic-personal-robots"],
  ["/soft-robotics", "/robot-types/soft-robotics"],
  ["/swarm-robotics", "/robot-types/swarm-robotics"],
  ["/underwater-robots", "/robot-types/underwater-robots"],
  ["/space-robotics", "/robot-types/space-robotics"],
  ["/exoskeletons", "/robot-types/exoskeletons"],
  ["/micro-robots-nanorobots", "/robot-types/micro-robots-nanorobots"],
  ["/autonomous-vehicles-delivery-robots", "/robot-types/autonomous-vehicles-delivery-robots"],
  ["/educational-robots", "/robot-types/educational-robots"],
  ["/inspection-maintenance-robots", "/robot-types/inspection-maintenance-robots"],
  ["/rescue-safety-robots", "/robot-types/rescue-safety-robots"],
  ["/autonomous-robot-companies", "/robotics-search-topics"],
  ["/autonomous-robots-companies", "/robotics-search-topics"],
  ["/autonomous-robotics-companies", "/robotics-search-topics"],
  ["/robot-manufacturers", "/robotics-companies"],
  ["/robotic-manufacturers", "/robotics-companies"],
  ["/robotics-manufacturers", "/robotics-companies"],
  ["/robots-hardware", "/robot-components"],
  ["/hardware-robots", "/robot-components"],
  ["/robotics-hardware", "/robot-components"],
  ["/robot-componenets", "/robot-components"],
  ["/robotic-arm-joint-component", "/robotic-arm-joint-components"],
  ["/robot-arm-joint-components", "/robotic-arm-joint-components"],
  ["/automated-micro-robotics", "/micro-robotics"],
  ["/robotic-components", "/robot-components"],
  ["/robotics-components", "/robot-components"],
  ["/robotic-component", "/robot-components"],
  ["/industrial-robot-components", "/robot-components"],
  ["/basic-components-of-a-robot", "/robot-components"],
  ["/construction-robot", "/construction-robotics"],
  ["/construction-site-robots", "/construction-robotics"],
  ["/ai-micro-robotics", "/micro-robotics"],
  ["/smart-micro-robotics", "/micro-robotics"],
  ["/robotic-software", "/robotics-software"],
  ["/robot-management-system", "/robot-management-software"],
  ["/robot-agnostic-management-software", "/robot-management-software"],
  ["/software-robotics", "/robotics-software"],
  ["/robotics-conference", "/robotics-conferences"],
  ["/robotic-conference", "/robotics-conferences"],
  ["/robotik-konferenz", "/robotics-events/conferences"],
  ["/robotic-convention", "/robotics-events/exhibitions"],
  ["/robotics-convention", "/robotics-events/exhibitions"],
  ["/robots-for-decommissioning", "/nuclear-robots"],
  ["/nuclear-robot", "/nuclear-robots"],
  ["/robotics-data", "/robotics-data-collection"],
  ["/physical-ai-data", "/robotics-data-collection"],
  ["/technia", "/about-techniahq"],
  ["/techniatranscat", "/about-techniahq"],
  ["/cleaning-robot-management-software", "/robot-management-software"],
  ["/robot-agnostic-cleaning-software", "/robot-management-software"],
  ["/what-is-robot-agnostic-cleaning-software", "/robot-management-software"],
  ["/data-for-robotics", "/robotics-data-collection"],
  ["/data-collection-for-robotics", "/robotics-data-collection"],
  ["/robotics-dataset", "/robotics-data-collection"],
  ["/robotics-training-data-services", "/robotics-data-collection"],
  ["/cleaning-robot-monitoring-software", "/robot-management-software"],
  ["/custodial-robot-management-platform", "/robot-management-software"],
  ["/janitorial-robot-management-software", "/robot-management-software"],
  ["/best-software-for-managing-cleaning-robots", "/robot-management-software"],
  ["/buy-cleaning-robot-management-software", "/robot-management-software"],
  ["/what-software-manages-autonomous-cleaning-robots", "/robot-management-software"],
  ["/robot-data-collection-platform", "/robotics-data-collection"],
  ["/robotics-training-data", "/robotics-data-collection"],
  ["/robot-training-data", "/robotics-data-collection"],
  ["/humanoid-robot-data-collection", "/robotics-data-collection"],
  ["/humanoid-robotics-data-collection", "/robotics-data-collection"],
  ["/ai-data-collection-company-for-robotics-training", "/robotics-data-collection"],
  ["/robotics-oem", "/robotics-companies"],
  ["/robotics-company", "/robotics-companies"],
  ["/robotics-development-company", "/robotics-companies"],
  ["/robotics-component-manufacturer", "/robot-components"],
  ["/robotic-arm-bearing-materials", "/robotic-arm-joint-components"],
  ["/robotic-arm-linkage-bearings", "/robotic-arm-joint-components"],
  ["/ai-safety", "/robotics-topics"],
  ["/robotics-research-topics", "/robotics-topics"],
  ["/robotics-fields", "/robotics-topics"],
  ["/entertainment-robots-market", "/entertainment-robotics"]
];

export default function App() {
  const location = useLocation();
  const isAdminPath = location.pathname === "/admin" || location.pathname.startsWith("/admin/");

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/es" element={<LocalizedEditionHome locale="es" />} />
        <Route path="/pt" element={<LocalizedEditionHome locale="pt" />} />
        {searchConsoleHubPages.map((page) => (
          <Route key={page.path} path={page.path} element={<SearchConsoleHubPage />} />
        ))}
        <Route path="/zh-cn/ai/:slug" element={<AIArticlePage locale="zh-cn" />} />
        <Route path="/ja/ai/:slug" element={<AIArticlePage locale="ja" />} />
        <Route path="/fr/ai/:slug" element={<AIArticlePage locale="fr" />} />
        <Route path="/de/ai/:slug" element={<AIArticlePage locale="de" />} />
        <Route path="/es/ai/:slug" element={<AIArticlePage locale="es" />} />
        <Route path="/pt/ai/:slug" element={<AIArticlePage locale="pt" />} />
        <Route path="/zh-cn/*" element={<LocalizedPriorityPage locale="zh-cn" />} />
        <Route path="/ja/*" element={<LocalizedPriorityPage locale="ja" />} />
        <Route path="/fr/*" element={<LocalizedPriorityPage locale="fr" />} />
        <Route path="/de/*" element={<LocalizedPriorityPage locale="de" />} />
        <Route path="/robots" element={<RobotTypes />} />
        <Route path="/humanoid-robots" element={<HumanoidCatalogPage />} />
        <Route path="/shop/humanoid-robots" element={<ShopHumanoidRobots />} />
        <Route path="/humanoid-intelligence" element={<HumanoidIntelligenceHub />} />
        <Route path="/humanoid-robot-company-growth" element={<HumanoidCompanyGrowthPage />} />
        <Route path="/humanoid-robots/database" element={<HumanoidDatabasePage />} />
        <Route path="/humanoid-robots/compare" element={<HumanoidComparePage />} />
        <Route path="/china-humanoid-model-tracker" element={<HumanoidTrackerPage type="china-models" />} />
        <Route path="/china-humanoid-order-tracker" element={<HumanoidTrackerPage type="china-orders" />} />
        <Route path="/china-humanoid-shipment-tracker" element={<HumanoidTrackerPage type="china-shipments" />} />
        <Route path="/humanoid-robot-deployment-map" element={<HumanoidTrackerPage type="deployments" />} />
        <Route path="/humanoid-robot-failures" element={<HumanoidTrackerPage type="failures" />} />
        <Route path="/china-humanoid-component-supply-chain" element={<HumanoidTrackerPage type="components" />} />
        <Route path="/physical-ai/china-model-tracker" element={<PhysicalAIIntelligencePage />} />
        <Route path="/data" element={<DataHub />} />
        <Route path="/data/physical-ai-benchmarks" element={<PhysicalAIIntelligencePage />} />
        <Route path="/data/physical-ai-real-robot-tests" element={<PhysicalAIIntelligencePage />} />
        <Route path="/data/physical-ai-training-data" element={<PhysicalAIIntelligencePage />} />
        <Route path="/data/physical-ai-inference-latency" element={<PhysicalAIIntelligencePage />} />
        <Route path="/data/physical-ai-deployments" element={<PhysicalAIIntelligencePage />} />
        <Route path="/data/physical-ai-failures" element={<PhysicalAIIntelligencePage />} />
        <Route path="/jobs" element={<JobsIntelligencePage />} />
        <Route path="/jobs/physical-ai" element={<JobsIntelligencePage />} />
        <Route path="/jobs/humanoid-robotics" element={<JobsIntelligencePage />} />
        <Route path="/jobs/china-embodied-ai" element={<JobsIntelligencePage />} />
        <Route path="/data/physical-ai-hiring-index" element={<JobsIntelligencePage />} />
        <Route path="/data/physical-ai-skills" element={<JobsIntelligencePage />} />
        <Route path="/data/physical-ai-salaries" element={<JobsIntelligencePage />} />
        <Route path="/jobs/vla-engineer" element={<JobsIntelligencePage />} />
        <Route path="/jobs/world-model-robotics" element={<JobsIntelligencePage />} />
        <Route path="/data/robotics-talent-moves" element={<JobsIntelligencePage />} />
        <Route path="/data/physical-ai-universities" element={<JobsIntelligencePage />} />
        <Route path="/physical-ai" element={<AuthorityHubPage />} />
        <Route path="/robotics" element={<RoboticsTopics />} />
        <Route path="/robotics/humanoid-robot-data" element={<HumanoidInfographicHub />} />
        {humanoidInfographicIndex.map((item) => (
          <Route key={item.slug} path={item.path} element={<HumanoidInfographicPage slug={item.slug} />} />
        ))}
        <Route path="/robotics/robotic-arm-cost-2026" element={<RoboticsArticlePage />} />
        <Route path="/robotics/da-vinci-surgical-robot-guide" element={<RoboticsArticlePage />} />
        <Route path="/robotics/fanuc-vs-abb-vs-kuka-vs-yaskawa" element={<RoboticsArticlePage />} />
        <Route path="/robotics/best-exoskeleton-robots-2026" element={<RoboticsArticlePage />} />
        <Route path="/robotics/warehouse-automation-cost-roi" element={<RoboticsArticlePage />} />
        <Route path="/robotics/best-robotics-courses-certifications" element={<RoboticsArticlePage />} />
        <Route path="/robotics/autonomous-delivery-robots-compared" element={<RoboticsArticlePage />} />
        <Route path="/robotics/:slug" element={<RoboticsXArticlePage />} />
        <Route path="/drones-plus-robotics" element={<RoboticsSeoClusterPage />} />
        <Route path="/micro-robotics" element={<RoboticsSeoClusterPage />} />
        <Route path="/entertainment-robotics" element={<RoboticsSeoClusterPage />} />
        <Route path="/robotics-conferences" element={<RoboticsSeoClusterPage />} />
        <Route path="/construction-robotics" element={<RoboticsSeoClusterPage />} />
        <Route path="/robot-types" element={<RoboticsSeoClusterPage />} />
        <Route path="/robot-types/:slug" element={<RobotCategoryPage />} />
        <Route path="/humanoid-robots/manufacturers" element={<HumanoidManufacturersPage />} />
        <Route path="/humanoid-robots/manufacturer/:manufacturerSlug" element={<HumanoidManufacturerPage />} />
        <Route path="/humanoid-robots/countries" element={<HumanoidCountriesPage />} />
        <Route path="/humanoid-robots/country/:countrySlug" element={<HumanoidCountryPage />} />
        <Route path="/humanoid-robots/france" element={<FrenchHumanoidRobots />} />
        <Route path="/humanoid-robots/japan" element={<JapaneseHumanoidRobots />} />
        <Route path="/humanoid-robots/china" element={<ChineseHumanoidRobots />} />
        <Route path="/humanoid-robots/united-states" element={<AmericanHumanoidRobots />} />
        <Route path="/humanoid-robots/:robotSlug" element={<HumanoidRobotPage />} />
        <Route path="/robot-sensors" element={<RobotSensors />} />
        <Route path="/robot-components" element={<RobotComponentsDirectory />} />
        <Route path="/robot-components/:slug" element={<RobotComponentRoutePage />} />
        <Route path="/robotics-software" element={<RoboticsSoftwareDirectory />} />
        <Route path="/robotics-software/robotics-programming-languages" element={<RoboticsArticlePage />} />
        <Route path="/robotics-software/visual-slam-vs-lidar-slam" element={<RoboticsArticlePage />} />
        <Route path="/robotics-software/:slug" element={<RoboticsSoftwareRoutePage />} />
        <Route path="/robotics-topics" element={<RoboticsTopics />} />
        <Route path="/robotics-companies" element={<RoboticsCompaniesDirectory />} />
        <Route path="/robotics-companies/country/:countrySlug" element={<RoboticsCompanyCountryPage />} />
        <Route path="/robotics-companies/:slug" element={<RoboticsCompanyRoutePage />} />
        <Route path="/robotics-events" element={<RoboticsEvents />} />
        <Route path="/robotics-events/:section" element={<RoboticsEvents />} />
        <Route path="/admin" element={<Navigate to="/admin/community" replace />} />
        <Route path="/admin/community" element={<Suspense fallback={<main className="site-shell py-16"><p className="font-bold text-neutral-600">Loading administration…</p></main>}><AdminCommunityPage /></Suspense>} />
        <Route path="/robot-component-price-guide" element={<Navigate to="/robot-components" replace />} />
        <Route path="/robotics-news" element={<RoboticsNews />} />
        <Route path="/humanoid-robot-safety-standards" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-reliability" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-operating-systems" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-controller-architecture" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-cybersecurity" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-fleet-management" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-modular-interfaces" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-dataset-standards" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-testing-standards" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-energy-efficiency" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-thermal-management" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-fall-protection" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-hand-durability" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-joint-reliability" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-maintenance" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-calibration" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-acceptance-testing" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-factory-integration" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-privacy" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-ota-updates" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-skill-management" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-interoperability" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-network-latency" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-autonomy-levels" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-liability-insurance" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-eu-regulation" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-simulation-testing" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-human-object-handover" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-incidents" element={<RoboticsArticlePage />} />
        <Route path="/humanoid-robot-deployment-readiness" element={<RoboticsArticlePage />} />
        <Route path="/articles/:slug" element={<RoboticsArticlePage />} />
        <Route path="/ai-news" element={<AINews />} />
        <Route path="/ai-news/:slug" element={<AIArticlePage />} />
        <Route path="/ai/:slug" element={<AIArticlePage />} />
        <Route path="/guides/:slug" element={<AIArticlePage />} />
        <Route path="/artificial-intelligence/grok-4-5-openai-claude-comparison" element={<Navigate to="/ai/grok-4-5-openai-claude-comparison" replace />} />
        <Route path="/artificial-intelligence/gpt-5-6-grok-claude-comparison" element={<Navigate to="/ai/gpt-5-6-grok-claude-comparison" replace />} />
        <Route path="/robotics-news/build-first-robot-limited-budget" element={<BuildFirstRobotLimitedBudget />} />
        <Route path="/robotics-news/22-countries-global-robotics" element={<RoboticsWorldCountries />} />
        <Route path="/robotics-news/:slug" element={<RoboticsArticlePage />} />
        <Route path="/viral-robotics-topics" element={<ViralTopics />} />
        <Route path="/robotics-search-topics" element={<SearchConsoleTopics />} />
        <Route path="/robotics-data-collection" element={<RoboticsSeoClusterPage />} />
        <Route path="/robot-management-software" element={<RoboticsSeoClusterPage />} />
        <Route path="/robotic-arm-joint-components" element={<RoboticArmJointComponents />} />
        <Route path="/nuclear-robots" element={<NuclearRobots />} />
        <Route path="/about-techniahq" element={<About />} />
        <Route path="/about" element={<Navigate to="/about-techniahq" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/terms" element={<Navigate to="/terms-of-use" replace />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/editorial-policy" element={<EditorialPolicy />} />
        {legacyRedirects.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdminPath && <SearchConsoleLinkSection />}
    </Layout>
  );
}
